/**
 * Variable Index Dynamic Average (VIDYA)
 *
 * Adaptive moving average using Chande Momentum Oscillator (CMO) as
 * the volatility index. Smoothing factor adapts to trend strength.
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource } from "./sma";

export const vidyaIndicator: IndicatorDefinition = {
  id: "vidya",
  name: "Variable Index Dynamic Average",
  shortName: "VIDYA",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "length", label: "CMO Length", type: "number", default: 9, min: 2, max: 200 },
    { key: "smoothing", label: "EMA Smoothing", type: "number", default: 12, min: 2, max: 200 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
    ]},
    { key: "color", label: "Color", type: "color", default: "#9C27B0" },
  ],
  plots: [
    { key: "vidya", label: "VIDYA", color: "#9C27B0", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { length, smoothing, source = "close" } = params;
    const src = getSource(candles, source);
    const data: { time: number; value: number }[] = [];

    if (src.length < Math.max(length, smoothing) + 1) return [{ key: "vidya", data }];

    const k = 2 / (smoothing + 1);
    let vidya = src[0];

    for (let i = 1; i < src.length; i++) {
      if (i >= length) {
        let sumUp = 0, sumDown = 0;
        for (let j = i - length + 1; j <= i; j++) {
          const diff = src[j] - src[j - 1];
          if (diff > 0) sumUp += diff;
          else sumDown += Math.abs(diff);
        }
        const cmo = (sumUp + sumDown) === 0 ? 0 : Math.abs((sumUp - sumDown) / (sumUp + sumDown));
        vidya = src[i] * k * cmo + vidya * (1 - k * cmo);
        data.push({ time: candles[i].time, value: vidya });
      }
    }

    return [{ key: "vidya", data }];
  },
};
